(() => {
  if (customElements.get('gsn-bundle')) return;
  const colors = new Set(['black', 'bordeaux', 'chocolate', 'khaki']);
  const normalize = (value) => String(value ?? '').trim().toLowerCase();
  const optionIndex = (options, names) => options.findIndex((name) => names.includes(normalize(name)));
  const priceValid = (price) => Number.isSafeInteger(price) && price > 0;

  customElements.define('gsn-bundle', class extends HTMLElement {
    connectedCallback() {
      this.owner = this.closest('product-info');
      this.form = this.owner?.querySelector('#' + CSS.escape(this.dataset.formId));
      if (!this.owner || !this.form) return;
      try {
        this.tops = JSON.parse(this.dataset.topVariants);
        this.pants = JSON.parse(this.dataset.pantsVariants);
        this.topColor = optionIndex(JSON.parse(this.dataset.topOptions), ['color', 'colour', 'farbe']);
        const pantsOptions = JSON.parse(this.dataset.pantsOptions);
        this.pantsColor = optionIndex(pantsOptions, ['color', 'colour', 'farbe']);
        this.pantsSize = optionIndex(pantsOptions, ['size', 'größe', 'groesse']);
        this.money = new Intl.NumberFormat(document.documentElement.lang || 'de-DE', {
          style: 'currency', currency: this.dataset.currency
        });
      } catch (_) { return; }
      if (this.topColor < 0 || this.pantsColor < 0 || this.pantsSize < 0) return;
      this.select = this.querySelector('[data-size]');
      this.button = this.querySelector('[data-add]');
      this.discount = this.dataset.discountEnabled === 'true';
      this.onChange = (event) => {
        if (event.target === this.select) this.renderPrice();
        else if (event.target === this.form.querySelector('[name="id"]')) {
          this.pending = false;
          this.sync();
        } else if (event.target.closest('variant-selects')) {
          this.pending = true;
          this.button.disabled = true;
          this.querySelector('[data-prices]').hidden = true;
          this.querySelector('[data-status]').textContent = 'Auswahl wird aktualisiert …';
        }
      };
      this.onAdd = () => this.add();
      this.owner.addEventListener('change', this.onChange);
      this.button.addEventListener('click', this.onAdd);
      if (typeof subscribe === 'function' && typeof PUB_SUB_EVENTS !== 'undefined') {
        this.unsubscribe = subscribe(PUB_SUB_EVENTS.variantChange, ({ data }) => {
          if (String(data.sectionId) !== String(this.owner.dataset.section)) return;
          const index = this.tops.findIndex((variant) => variant.id === data.variant?.id);
          if (index >= 0) this.tops[index] = data.variant;
          this.pending = false;
          this.sync();
        });
      }
      this.sync();
    }

    disconnectedCallback() {
      this.owner?.removeEventListener('change', this.onChange);
      this.button?.removeEventListener('click', this.onAdd);
      this.unsubscribe?.();
    }

    selection() {
      const id = this.form.querySelector('[name="id"]')?.value;
      const top = this.tops.find((variant) => String(variant.id) === id);
      const color = normalize(top?.options[this.topColor]);
      const pants = this.pants.find((variant) => String(variant.id) === this.select.value);
      const valid = Boolean(!this.pending && top?.available && pants?.available && colors.has(color)
        && normalize(pants.options[this.pantsColor]) === color
        && priceValid(top.price) && priceValid(pants.price));
      return { top, pants, color, valid };
    }

    sync() {
      const { top, color } = this.selection();
      this.hidden = !top || !colors.has(color);
      if (this.hidden) {
        this.select.value = '';
        this.button.disabled = true;
        return;
      }
      const oldValue = this.currentColor === color ? this.select.value : '';
      this.currentColor = color;
      this.querySelector('[data-color]').textContent = top.options[this.topColor];
      this.select.replaceChildren(new Option('Bitte wählen', ''));
      const matching = this.pants.filter((variant) => normalize(variant.options[this.pantsColor]) === color);
      const image = this.querySelector('[data-pants-image]');
      const imageUrl = this.dataset['image' + color[0].toUpperCase() + color.slice(1)];
      image.hidden = !imageUrl;
      if (imageUrl) {
        image.src = imageUrl;
        image.alt = `Jogger ${top.options[this.topColor]} – Vorderansicht`;
      }
      const prices = matching.filter((variant) => variant.available && priceValid(variant.price)).map((variant) => variant.price);
      const preview = this.querySelector('[data-pants-preview-price]');
      const minimum = Math.min(...prices);
      const saving = this.discount ? Math.floor(minimum * 15 / 100) : 0;
      preview.textContent = prices.length ? `${new Set(prices).size > 1 ? 'Ab ' : ''}${this.money.format((minimum - saving) / 100)}${this.discount ? ' im Set' : ''}` : '';
      for (const variant of matching) {
        const size = variant.options[this.pantsSize];
        const option = new Option(`${size}${variant.available ? '' : ' – ausverkauft'}`, String(variant.id));
        option.disabled = !variant.available;
        this.select.add(option);
      }
      if (matching.some((variant) => String(variant.id) === oldValue && variant.available)) this.select.value = oldValue;
      this.hasPants = matching.some((variant) => variant.available);
      this.select.disabled = this.busy || !this.hasPants || !top.available;
      this.renderPrice();
    }

    renderPrice() {
      const { top, pants, valid } = this.selection();
      this.button.disabled = !valid || this.busy || this.uncertain;
      this.querySelector('[data-prices]').hidden = !valid;
      this.querySelector('[data-discount-note]').hidden = !this.discount || !valid;
      this.querySelector('[data-status]').textContent = !top?.available
        ? 'Das gewählte Oberteil ist ausverkauft.'
        : !this.hasPants ? 'Die passende Hose ist in dieser Farbe derzeit ausverkauft.'
        : !valid ? 'Wähle deine Hosengröße.' : '';
      if (!valid) return;
      // One pair: verified Shopify checkout truncates the saving (6999 -> 1049 cents).
      const saving = this.discount ? Math.floor(pants.price * 15 / 100) : 0;
      const values = {
        'top-price': top.price, 'pants-original': pants.price,
        'pants-price': pants.price - saving, saving: -saving,
        total: top.price + pants.price - saving
      };
      for (const [key, value] of Object.entries(values)) this.querySelector(`[data-${key}]`).textContent = this.money.format(value / 100);
      this.querySelector('[data-pants-original]').hidden = !this.discount;
      this.querySelector('[data-saving-row]').hidden = !this.discount;
    }

    async add() {
      const { top, pants, valid } = this.selection();
      if (!valid || this.busy || this.uncertain) return;
      this.busy = true;
      this.setAttribute('aria-busy', 'true');
      this.button.disabled = true;
      this.select.disabled = true;
      this.button.textContent = 'Wird hinzugefügt …';
      this.querySelector('[data-error]').hidden = true;
      const controller = new AbortController();
      const timeout = setTimeout(() => controller.abort(), 20000);
      const root = window.Shopify?.routes?.root || '/';
      try {
        const response = await fetch(`${root}cart/add.js`, {
          method: 'POST', credentials: 'same-origin', signal: controller.signal,
          headers: { 'Content-Type': 'application/json', Accept: 'application/json' },
          body: JSON.stringify({ items: [{ id: top.id, quantity: 1 }, { id: pants.id, quantity: 1 }] })
        });
        const data = await response.json();
        if (!response.ok || data.status || !Array.isArray(data.items) || data.items.length !== 2) {
          throw new Error('cart-add-failed');
        }
        // Full cart navigation avoids stale drawer sections, totals or discount labels.
        window.location.assign(window.routes?.cart_url || `${root}cart`);
      } catch (_) {
        // A timeout or 422 can leave a changed cart. Never retry or roll it back blindly.
        this.uncertain = true;
        const error = this.querySelector('[data-error]');
        error.textContent = 'Beide Artikel konnten nicht sicher bestätigt werden. Bitte prüfe deinen Warenkorb und die Verfügbarkeit, bevor du erneut hinzufügst.';
        error.hidden = false;
        this.querySelector('[data-cart-link]').hidden = false;
        this.button.textContent = 'Bitte Warenkorb prüfen';
        this.removeAttribute('aria-busy');
        this.select.disabled = false;
      } finally {
        clearTimeout(timeout);
        // Keep add locked until navigation, including after an ambiguous server response.
      }
    }
  });
})();

(() => {
  if (window.gsnSelleasyAdapter) return;
  const configNode = document.getElementById('GSNSelleasyConfig');
  const nativeBundle = document.querySelector('gsn-bundle[data-pants-variants]');
  if (!configNode && !nativeBundle) return;
  let config;
  try {
    config = configNode ? JSON.parse(configNode.textContent) : {
      productId: nativeBundle.closest('product-info')?.dataset.productId,
      topOptions: JSON.parse(nativeBundle.dataset.topOptions),
      topVariants: JSON.parse(nativeBundle.dataset.topVariants),
      pantsOptions: JSON.parse(nativeBundle.dataset.pantsOptions),
      pantsVariants: JSON.parse(nativeBundle.dataset.pantsVariants),
      images: {
        black: nativeBundle.dataset.imageBlack, bordeaux: nativeBundle.dataset.imageBordeaux,
        chocolate: nativeBundle.dataset.imageChocolate, khaki: nativeBundle.dataset.imageKhaki
      }
    };
  } catch (_) { return; }
  const normalize = value => String(value ?? '').trim().toLowerCase();
  const colorNames = ['farbe', 'color', 'colour'];
  const indexOf = (options, names) => options.findIndex(name => names.includes(normalize(name)));
  const topColor = indexOf(config.topOptions, colorNames);
  const pantsColor = indexOf(config.pantsOptions, colorNames);
  const pantsSize = indexOf(config.pantsOptions, ['größe', 'groesse', 'size']);
  if ([topColor, pantsColor, pantsSize].some(index => index < 0)) return;
  window.gsnSelleasyAdapter = true;
  document.documentElement.classList.toggle('gsn-selleasy-managed', true);
  const allowedColors = new Set(['black', 'bordeaux', 'chocolate', 'khaki']);
  const pantsById = new Map(config.pantsVariants.map(variant => [String(variant.id), variant]));
  const originalDisabled = new WeakMap();
  const pending = new WeakSet();
  const badImages = new Set();
  const wiredImages = new WeakSet();
  let queued = false;
  let running = false;
  const flag = (node, name, enabled) => {
    if (node.hasAttribute(name) !== enabled) node.toggleAttribute(name, enabled);
  };
  function owner() {
    return [...document.querySelectorAll('product-info')].find(node =>
      node.querySelector('input[name="id"]') &&
      (!config.productId || !node.dataset.productId || String(node.dataset.productId) === String(config.productId)));
  }
  function currentColor(info) {
    for (const field of info.querySelectorAll('fieldset')) {
      if (!colorNames.includes(normalize(field.querySelector('legend')?.textContent))) continue;
      const checked = field.querySelector('input[type="radio"]:checked');
      if (checked) return normalize(checked.value);
    }
    const id = info.querySelector('input[name="id"]')?.value;
    return normalize(config.topVariants.find(variant => String(variant.id) === id)?.options[topColor]);
  }
  function offers() {
    return [...document.querySelectorAll('select.lb-variant-picker')].map(select => {
      const card = select.closest('lb-upsell-flat-card.lb-product-card');
      if (card?.getAttribute('lb-product-id') !== '16119656153413') return null;
      const checkboxes = card.querySelectorAll('input[type="checkbox"]');
      const checkbox = checkboxes.length === 1 ? checkboxes[0] : null;
      return checkbox ? { select, card, checkbox } : null;
    }).filter(Boolean);
  }
  function deselect(checkbox) {
    if (checkbox.checked) checkbox.click();
  }
  function later() {
    if (queued) return;
    queued = true;
    requestAnimationFrame(() => { queued = false; sync(); });
  }
  function savingNote(card, variant, ready) {
    let note = card.querySelector('[data-gsn-selleasy-saving]');
    const bundle = document.querySelector('gsn-bundle[data-pants-variants]');
    const actions = card.querySelector('.lb-cta-cont')?.querySelector('.lb-cta-actions');
    if (!ready || bundle?.dataset.discountEnabled !== 'true' || !actions ||
        !Number.isSafeInteger(variant?.price) || variant.price <= 0 || !bundle.dataset.currency) {
      note?.remove();
      return;
    }
    let money;
    try { money = new Intl.NumberFormat('de-DE', { style: 'currency', currency: bundle.dataset.currency }); }
    catch (_) { note?.remove(); return; }
    const reducedPrice = variant.price - Math.floor(variant.price * 15 / 100);
    const text = '15 % auf die Hose im Set: ' + money.format(reducedPrice / 100) + ' statt ' + money.format(variant.price / 100);
    if (!note) {
      note = document.createElement('p');
      note.className = 'gsn-selleasy-saving';
      note.setAttribute('data-gsn-selleasy-saving', '');
      note.setAttribute('aria-live', 'polite');
      actions.insertAdjacentElement('afterend', note);
    }
    if (note.textContent !== text) note.textContent = text;
  }
  function sync() {
    if (running) { later(); return; }
    running = true;
    let ready = false;
    try {
      const info = owner();
      const color = info ? currentColor(info) : '';
      const addons = new Set(document.querySelectorAll('lb-addon[data-gsn-selleasy-empty]'));
      for (const { select, card, checkbox } of offers()) {
        const addon = card.closest('lb-addon');
        if (addon) addons.add(addon);
        const usable = [];
        for (const option of select.options) {
          if (!originalDisabled.has(option)) originalDisabled.set(option, option.disabled);
          const variant = pantsById.get(option.value);
          const sameColor = allowedColors.has(color) && normalize(variant?.options[pantsColor]) === color;
          const available = sameColor && variant.available && Number.isSafeInteger(variant.price) && variant.price > 0;
          const disabled = !available || originalDisabled.get(option);
          if (option.disabled !== disabled) option.disabled = disabled;
          if (option.hidden !== !sameColor) option.hidden = !sameColor;
          flag(option, 'data-gsn-color-blocked', !sameColor);
          if (!disabled) usable.push(variant);
        }
        const selected = pantsById.get(select.value);
        const selectedUsable = usable.some(variant => String(variant.id) === select.value);
        const images = card.querySelectorAll('img');
        const image = images.length === 1 ? images[0] : null;
        const imageUrl = config.images[color];
        if (!usable.length || !image || !imageUrl || badImages.has(imageUrl)) {
          flag(card, 'data-gsn-selleasy-ready', false);
          flag(card, 'data-gsn-selleasy-blocked', true);
          deselect(checkbox);
          savingNote(card, null, false);
          continue;
        }
        flag(card, 'data-gsn-selleasy-blocked', false);
        if (!selectedUsable) {
          deselect(checkbox);
          const previousSize = selected?.options[pantsSize];
          const next = usable.find(variant => variant.options[pantsSize] === previousSize) || usable[0];
          select.value = String(next.id);
          pending.add(select);
          select.dispatchEvent(new Event('change', { bubbles: true }));
          requestAnimationFrame(() => { pending.delete(select); later(); });
        }
        if (!wiredImages.has(image)) {
          wiredImages.add(image);
          image.addEventListener('load', later);
          image.addEventListener('error', () => {
            const failed = image.getAttribute('data-gsn-selleasy-image');
            if (failed) badImages.add(failed);
            later();
          });
        }
        if (image.getAttribute('data-gsn-selleasy-image') !== imageUrl) image.setAttribute('data-gsn-selleasy-image', imageUrl);
        if (image.getAttribute('srcset')) image.removeAttribute('srcset');
        if (image.getAttribute('src') !== imageUrl) image.setAttribute('src', imageUrl);
        const variant = pantsById.get(select.value);
        const alt = 'Jogger ' + (variant?.options[pantsColor] || color) + ' – Studioansicht';
        if (image.alt !== alt) image.alt = alt;
        const isReady = !pending.has(select) && !select.disabled && !checkbox.disabled &&
          usable.some(item => String(item.id) === select.value);
        flag(card, 'data-gsn-selleasy-ready', isReady);
        savingNote(card, variant, isReady);
        ready ||= isReady;
      }
      for (const addon of addons) {
        const cards = [...addon.querySelectorAll('lb-upsell-flat-card.lb-product-card')];
        const empty = cards.length > 0 && cards.every(card => card.hasAttribute('data-gsn-selleasy-blocked'));
        flag(addon, 'data-gsn-selleasy-empty', empty);
      }
      document.documentElement.classList.toggle('gsn-selleasy-ready', ready);
    } finally { running = false; }
  }
  document.addEventListener('change', event => {
    if (event.target.closest('product-info') || event.target.closest('lb-embed')) {
      const notice = document.querySelector('[data-gsn-selleasy-notice]');
      if (notice) notice.hidden = true;
      sync();
    }
  }, true);
  function guard(event) {
    const info = owner();
    if (!info || !info.contains(event.target)) return;
    if (event.type === 'click' && !event.target.closest('button[name="add"], button[type="submit"]')) return;
    const color = currentColor(info);
    const unsafe = offers().some(({ select, card, checkbox }) => checkbox.checked &&
      (!card.hasAttribute('data-gsn-selleasy-ready') ||
       normalize(pantsById.get(select.value)?.options[pantsColor]) !== color));
    sync();
    if (unsafe) {
      event.preventDefault();
      event.stopImmediatePropagation();
      let notice = info.querySelector('[data-gsn-selleasy-notice]');
      if (!notice) {
        notice = document.createElement('p');
        notice.setAttribute('data-gsn-selleasy-notice', '');
        notice.setAttribute('role', 'status');
        const button = info.querySelector('button[name="add"]');
        (button?.parentElement || info).append(notice);
      }
      notice.textContent = 'Deine Hosenauswahl wurde aktualisiert. Bitte prüfe sie und füge deine Auswahl erneut hinzu.';
      notice.hidden = false;
    }
  }
  document.addEventListener('click', guard, true);
  document.addEventListener('submit', guard, true);
  let activation = null;
  function keyboardAdd(event) {
    if (!['Enter', ' '].includes(event.key) || event.isComposing || event.ctrlKey || event.altKey || event.metaKey) return;
    const previous = activation;
    if (event.type === 'keyup' && activation?.key === event.key) activation = null;
    const button = event.target.closest?.('button[name="add"][type="submit"]');
    const info = owner();
    if (!button || !info?.contains(button) || !button.closest('product-form') ||
        !offers().some(({ card }) => card.hasAttribute('data-gsn-selleasy-ready'))) return;
    event.preventDefault();
    event.stopImmediatePropagation();
    const click = () => {
      if (!button.disabled && button.getAttribute('aria-disabled') !== 'true') button.click();
    };
    if (event.type === 'keydown') {
      if (event.repeat || activation) return;
      activation = { key: event.key, button };
      if (event.key === 'Enter') click();
    } else if (event.key === ' ' && previous?.key === ' ' && previous.button === button) click();
  }
  window.addEventListener('keydown', keyboardAdd, true);
  window.addEventListener('keyup', keyboardAdd, true);
  window.addEventListener('blur', () => { activation = null; });

  new MutationObserver(later).observe(document.body, {
    childList: true, subtree: true, attributes: true,
    attributeFilter: ['value', 'checked', 'src', 'srcset', 'disabled', 'data-discount-enabled', 'data-currency']
  });
  document.addEventListener('shopify:section:load', later);
  sync();
})();
